import { ArrowRight, HandHeart, Sparkles, Sun } from "lucide-react";
import { ASSETS } from "../lib/constants";
import { useContent, resolveImage } from "../contexts/ContentContext";

export const Hero = () => {
  const { data: hero } = useContent("hero");
  if (!hero) return null;

  const stats = hero.stats || [];

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-32 sm:pt-36 lg:pt-40 pb-20 lg:pb-28 overflow-hidden"
    >
      <div className="absolute inset-0 hero-mesh hero-mesh-mask" aria-hidden="true" />
      <div className="absolute inset-0 dot-pattern opacity-40 hero-mesh-mask" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-7 animate-fade-up">
          <span data-testid="hero-eyebrow" className="section-eyebrow">
            <Sparkles className="w-3.5 h-3.5" />
            {hero.eyebrow}
          </span>

          <h1
            data-testid="hero-headline"
            className="mt-5 font-display text-4xl sm:text-5xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight"
          >
            <span className="text-gradient-brand">{hero.title_line1}</span>
            <br />
            <span className="text-[#0F172A]">{hero.title_line2}</span>
            <span className="text-gradient-gold">{hero.title_accent}</span>
          </h1>

          <p
            data-testid="hero-subhead"
            className="mt-6 max-w-xl text-base md:text-lg text-slate-600 leading-relaxed"
          >
            {hero.subhead}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a href={hero.cta_primary_href} data-testid="hero-get-involved-button" className="btn-gold">
              <HandHeart className="w-5 h-5" />
              {hero.cta_primary_label}
            </a>
            <a href={hero.cta_secondary_href} data-testid="hero-summer-button" className="btn-outline-blue">
              {hero.cta_secondary_label}
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-3 max-w-xl" data-testid="hero-stats">
            {stats.slice(0, 3).map((s, i) => (
              <div key={i} className="stat-chip">
                <p className="text-2xl md:text-3xl font-extrabold font-display text-[#2563EB]">{s.value}</p>
                <p className="text-[11px] md:text-xs text-slate-600 mt-1 leading-tight">{s.label}</p>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <img
              src={resolveImage(hero.foundation_logo_url) || ASSETS.foundationLogo}
              alt="Semaj J. Morgan Foundation"
              className="w-32 sm:w-36 h-auto"
              loading="eager"
              data-testid="hero-foundation-logo"
            />
          </div>
        </div>

        <div className="lg:col-span-5 relative animate-fade-up delay-200">
          <div className="relative aspect-square max-w-md mx-auto">
            <div aria-hidden="true" className="absolute -top-6 -right-4 w-72 h-72 bg-[#2563EB] rounded-[42%_58%_70%_30%/40%_50%_50%_60%] opacity-90 blur-[2px]" />
            <div aria-hidden="true" className="absolute -bottom-6 -left-6 w-56 h-56 bg-[#FFD100] rounded-[60%_40%_30%_70%/60%_30%_70%_40%] opacity-95" />

            <div className="relative w-full h-full rounded-[36px] overflow-hidden shadow-[0_30px_80px_rgba(15,23,42,0.18)] ring-1 ring-white bg-[#0F172A]">
              <img
                src={resolveImage(hero.image_url) || ASSETS.action.gala}
                alt="Semaj J. Morgan with family and supporters"
                className="w-full h-full object-cover"
                style={{ objectPosition: "center 25%" }}
                loading="eager"
                fetchpriority="high"
                data-testid="hero-image"
              />
              <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/60 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                <div className="bg-white/95 backdrop-blur rounded-2xl px-4 py-3 shadow-lg">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[#2563EB] font-bold">{hero.founder_tag}</p>
                  <p className="text-sm font-bold text-slate-900 font-display">{hero.founder_name}</p>
                </div>
              </div>
            </div>

            <div className="absolute -bottom-6 -right-2 sm:-right-6 animate-float-slow">
              <div className="bg-[#FFD100] text-[#0F172A] rounded-3xl px-5 py-4 shadow-[0_20px_50px_rgba(255,209,0,0.45)] ring-4 ring-white max-w-[180px]">
                <div className="flex items-center gap-2">
                  <Sun className="w-5 h-5" strokeWidth={2.5} />
                  <p className="text-[10px] font-bold uppercase tracking-[0.16em]">{hero.callout_eyebrow}</p>
                </div>
                <p className="mt-1.5 font-display text-base font-extrabold leading-tight">{hero.callout_title}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
